// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Button, Divider, Group, Loader, Paper, Stack, Text, Title } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { normalizeErrorString } from '@medplum/core';
import type { Patient, Questionnaire, QuestionnaireResponse } from '@medplum/fhirtypes';
import { QuestionnaireResponseDisplay, useMedplum, useMedplumProfile } from '@medplum/react';
import { IconDownload } from '@tabler/icons-react';
import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

const EXCLUDED_QUESTIONNAIRE_NAMES = new Set(['pcp-referral-provider-info']);

interface ScreenerEntry {
  questionnaire: Questionnaire;
  latestQR: QuestionnaireResponse;
}

export function IntakeScreenerPage(): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Patient | undefined;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState<ScreenerEntry[]>([]);

  const load = useCallback(async () => {
    if (!profile?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const allQRs = await medplum.searchResources('QuestionnaireResponse', {
        subject: `Patient/${profile.id}`,
        _sort: '-authored',
        _count: '200',
      });

      // Group by questionnaire reference, keeping the most recent QR per questionnaire
      const latestByQuestionnaire = new Map<string, QuestionnaireResponse>();
      for (const qr of allQRs) {
        if (!qr.questionnaire) {
          continue;
        }
        if (!latestByQuestionnaire.has(qr.questionnaire)) {
          latestByQuestionnaire.set(qr.questionnaire, qr);
        }
      }

      // Fetch each referenced Questionnaire and filter out excluded ones
      const results = await Promise.all(
        Array.from(latestByQuestionnaire.entries()).map(async ([questionnaireRef, latestQR]) => {
          const id = questionnaireRef.replace('Questionnaire/', '');
          const questionnaire = await medplum.readResource('Questionnaire', id).catch(() => null);
          if (!questionnaire) {
            return null;
          }
          if (EXCLUDED_QUESTIONNAIRE_NAMES.has(questionnaire.name ?? '')) {
            return null;
          }
          return { questionnaire, latestQR } satisfies ScreenerEntry;
        })
      );

      const sorted = (results.filter((e) => e !== null) as ScreenerEntry[]).sort((a, b) =>
        (a.latestQR.authored ?? '').localeCompare(b.latestQR.authored ?? '')
      );
      setEntries(sorted);
    } catch (err) {
      showNotification({ color: 'red', message: normalizeErrorString(err), autoClose: false });
    } finally {
      setLoading(false);
    }
  }, [medplum, profile]);

  useEffect(() => {
    load().catch(console.error);
  }, [load]);

  return (
    <Stack gap="lg" p="md" maw={860} mx="auto">
      <Group justify="space-between">
        <Title order={2}>Intake Screener</Title>
        <Button variant="subtle" onClick={() => navigate('/my-chart')}>
          Back to My Chart
        </Button>
      </Group>

      {loading && (
        <Stack align="center" py="xl">
          <Loader size="sm" />
        </Stack>
      )}

      {!loading && entries.length === 0 && (
        <Paper withBorder radius="md" p="xl">
          <Text c="dimmed">No intake screener responses found.</Text>
        </Paper>
      )}

      {!loading &&
        entries.map(({ questionnaire, latestQR }) => (
          <Paper key={questionnaire.id} withBorder radius="md" p="xl">
            <Group justify="space-between" align="flex-start" mb="sm">
              <Title order={3}>{questionnaire.title ?? questionnaire.name}</Title>
              <Button
                variant="light"
                size="sm"
                leftSection={<IconDownload size={16} />}
                onClick={() => downloadResponse(questionnaire, latestQR)}
              >
                Download
              </Button>
            </Group>
            <Divider mb="md" />
            <QuestionnaireResponseDisplay questionnaireResponse={latestQR} />
          </Paper>
        ))}
    </Stack>
  );
}

function downloadResponse(questionnaire: Questionnaire, response: QuestionnaireResponse): void {
  const payload = {
    questionnaire: questionnaire.title ?? questionnaire.name ?? questionnaire.id,
    response,
  };
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `intake-questionnaire-${response.authored?.slice(0, 10) ?? 'response'}.json`;
  anchor.click();
  URL.revokeObjectURL(url);
}
